import { Ban, CheckCircle2, History, Scale, ThumbsDown, Undo2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { Menu } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { Tooltip } from '../../../../components/Tooltip';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime, toIso } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { BudgetReviewCeilingModal } from '../components/BudgetReviewCeilingModal';
import { PlanExpendituresPanel } from '../components/PlanExpendituresPanel';
import { PpaRosterPanel } from '../components/PpaRosterPanel';
import { budgetPlansApi } from '../budgetPlansApi';
import { budgetReviewApi } from '../budgetReviewApi';
import { BUDGET_PLAN_STATUS, type BudgetPlanDetail } from '../budgetPlanTypes';
import { ppasApi } from '../ppasApi';
import type { BudgetPlanDepartmentRow } from '../ppaTypes';

type TabKey = 'info' | 'proposals' | 'expenditures';

/**
 * One plan under review — its budget proposals, and the verdict on them.
 *
 * On the **header** layout, like the plan's own profile: a plan reads as one continuous thing
 * rather than a record whose content splits into panels a reader chooses between.
 *
 * **It reads the PLAN and the PLAN's ROSTER**, not records of its own. There is no
 * budget-review row: what this module owns is the ceilings on that roster and the three
 * verdicts in the banner. The roster endpoint carries `budget-plans.read`, which a reader of
 * a plan under review necessarily holds, and the proposals panel is the very same
 * `<PpaRosterPanel>` the plan's PPA tab draws — one component over one DTO, which is the
 * reference's own shape and what stops two surfaces disagreeing about whether a department is
 * done.
 *
 * **What makes this the REVIEWER's copy of that panel is a callback**, not a flag: passing
 * `onSetCeiling` is what draws the row's `Update approved ceiling` entry, behind
 * `budget-review.set-ceiling`.
 */
export function BudgetReviewProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [detail, setDetail] = useState<BudgetPlanDetail | null>(null);
  const [roster, setRoster] = useState<BudgetPlanDepartmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [running, setRunning] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  /*
    IT LANDS ON THE PROPOSALS, not on Info — the reference's own `useState<BrTab>('departments')`.

    This is the document-profile exemption read one module along: a reviewer opens a plan to
    READ what the departments filed, which is the tab, where every other profile here is opened
    to check a record's own fields. `Info` is still the FIRST tab DECLARED, so the strip's order
    and the check `<ProfileHeaderShell>` makes in development are untouched — only where the
    page lands.
  */
  const [tab, setTab] = useState<TabKey>('proposals');
  const [pricing, setPricing] = useState<BudgetPlanDepartmentRow | null>(null);

  /*
    THE ROSTER IS FETCHED EAGERLY, with the record — the profile's two-tier rule read
    correctly rather than skipped. The header states how many departments are priced out of
    how many are enrolled and the tab badge states the same figure, so the roster IS the
    count: one fetch, one derivation, and the two cannot disagree.

    It fails SOFT. A failed roster must not take the Info tab down with it — the tab then
    reads empty and says so, exactly as an empty roster does.
  */
  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      budgetPlansApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setDetail(next);

          // Cleared on success, or a refused verdict would keep its banner over a page that
          // has since re-read the record and disagrees with it.
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this plan.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });

      ppasApi
        .roster(id, signal)
        .then((rows) => {
          if (!signal?.aborted) setRoster(rows);
        })
        .catch(() => {
          if (!signal?.aborted) setRoster([]);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /** One runner for all three verdicts — they differ only in which call they make. */
  async function run(action: (planId: string) => Promise<unknown>, failure: string) {
    if (!id) return;

    setRunning(true);
    setError(null);

    try {
      await action(id);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError(failure, 0));
    } finally {
      setRunning(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Procurement',
    'Investment Planning',
    { label: 'Budget Review', to: '/procurement/investment-planning/budget-review' },
    detail?.plan.planNumber ?? 'Budget review',
  ];

  // Guarded on having NOTHING to show, never on `error` alone: once a record is up it is
  // still the last good answer, and a refused verdict goes to the banner.
  if (error && !detail) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading || !detail) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const { plan } = detail;

  /*
    PRICED out of PROPOSED, derived from the roster rows the page already holds.

    The denominator is the departments that have PROPOSED something, not every enrolled
    office: an office that filed nothing has no ceiling to set and is not what holds an
    approval up, which is exactly how the server's own guard counts. Counting the whole
    roster would leave the header reading 12/60 on a plan nothing is blocking.
  */
  const proposing = roster.filter((row) => row.ppaCount > 0);
  const pricedCount = proposing.filter((row) => row.isPriced).length;
  const proposedTotal = roster.reduce((sum, row) => sum + row.total, 0);
  const approvedTotal = roster.reduce((sum, row) => sum + (row.approvedCeiling ?? 0), 0);

  const underReview =
    plan.status === BUDGET_PLAN_STATUS.submissionClosed ||
    plan.status === BUDGET_PLAN_STATUS.underReview;

  /*
    The APPROVE guard, mirrored from the server so the button says why rather than the reader
    meeting a refusal. It is the server that decides — the refusal names the same count — and
    this is the disabled reason, never a second rule: the client compares `isPriced`, which is
    the server's own answer sent on the row, instead of re-deriving it from two figures that
    can coincide.
  */
  const unpriced = proposing.length - pricedCount;

  const approveDisabledReason =
    proposing.length === 0
      ? 'No department has proposed anything under this plan, so there is nothing to approve.'
      : unpriced > 0
        ? `${unpriced} of ${proposing.length} departments do not have an approved ceiling matching their proposed budget.`
        : undefined;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="budget-review-profile"
        header={
          <ProfileHeader
            id="budget-review-profile-header"
            icon={<Scale className="h-6 w-6" aria-hidden="true" />}
            title={plan.planNumber}
            meta={[`FY ${plan.fiscalYear}`, plan.statusLabel]}
            /*
              TWO facts, and each is one the Info card does not state. The coverage and the
              proposed total moved INTO that card with the reference's row set, so repeating
              them here would be the same value in two slots on one screen. What stays is what
              the card has no row for: how far the review has got, and what it has approved.
            */
            secondary={[
              `${pricedCount}/${proposing.length} departments priced`,
              `${formatMoney(approvedTotal)} approved`,
            ]}
            /*
              The pill is the ROW's state — live or deleted — and the stage is in the meta line
              beside the year. Two pills would put one word against two meanings, which is the
              call the plan's own profile already takes.
            */
            status={
              <StatusText tone={plan.isActive ? 'active' : 'inactive'}>
                {plan.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={plan.createdByName}
            createdAt={plan.createdAt}
            actions={
              <Menu
                id="budget-review-profile-settings"
                items={[
                  {
                    key: 'history',
                    label: 'History Logs',
                    icon: <History className="h-4 w-4" aria-hidden="true" />,
                    onClick: () => setHistoryOpen(true),
                    permission: 'audit-logs.read',
                  },
                ]}
              />
            }
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="budget-review-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : underReview ? (
            /*
              THE THREE VERDICTS, and they live here and nowhere else: each acts on the whole
              record rather than on one card's fields. All three carry `budget-review.decide` —
              whoever may approve may reject and may return, so a key per direction would
              lengthen the permission picker without protecting anything new.
            */
            <ProfileBanner
              id="budget-review-profile-banner"
              icon={<Scale className="h-4 w-4" aria-hidden="true" />}
              message={
                proposing.length === 0
                  ? 'This plan is under review and no department has proposed anything under it. There is nothing to price and nothing to approve.'
                  : `This plan is under review. ${pricedCount} of ${proposing.length} departments that proposed a budget carry an approved ceiling on every fund they asked against.`
              }
              action={
                <PermissionGate permission="budget-review.decide">
                  <div className="flex items-center gap-2">
                    <Button
                      id="budget-review-profile-return"
                      variant="secondary"
                      leading={<Undo2 className="h-4 w-4" aria-hidden="true" />}
                      onClick={() =>
                        void run(
                          budgetReviewApi.returnForUpdate,
                          'Could not return this plan for update.',
                        )
                      }
                      disabled={running}
                      loading={running}
                      loadingLabel="Returning"
                    >
                      Return for update
                    </Button>

                    <Button
                      id="budget-review-profile-reject"
                      variant="secondary"
                      leading={<ThumbsDown className="h-4 w-4" aria-hidden="true" />}
                      onClick={() => void run(budgetReviewApi.reject, 'Could not reject this plan.')}
                      disabled={running}
                      loading={running}
                      loadingLabel="Rejecting"
                    >
                      Reject
                    </Button>

                    {/*
                      Disabled for STATE and the reason rides in the shared `<Tooltip>` —
                      every disabled control says why, and `<Button>` has no `disabledReason`
                      of its own.

                      **NOT `block`.** The child is an inline-flex `<Button>` in a row beside
                      Reject, and `block w-full` would take the whole action row. That prop is
                      for a full-width context — a menu row, a grid tile, a table cell — and
                      the comment here used to assert the opposite.
                    */}
                    <Tooltip disabledReason={approveDisabledReason}>
                      <Button
                        id="budget-review-profile-approve"
                        variant="primary"
                        leading={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
                        onClick={() =>
                          void run(budgetReviewApi.approve, 'Could not approve this plan.')
                        }
                        disabled={running || approveDisabledReason !== undefined}
                        loading={running}
                        loadingLabel="Approving"
                      >
                        Approve
                      </Button>
                    </Tooltip>
                  </div>
                </PermissionGate>
              }
            />
          ) : plan.status === BUDGET_PLAN_STATUS.finalized ? (
            /*
              EVERY state gets a banner, not only the ones needing attention — one that appears
              only when something is wrong trains the reader to skip the strip. Returning it
              for update is how a finalized plan is reopened, and it keeps every ceiling.
            */
            <ProfileBanner
              id="budget-review-profile-banner"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This plan is finalized. Its ceilings can no longer be set — returning it to the departments for update reopens the review and leaves every ceiling exactly as it is."
              action={
                <PermissionGate permission="budget-review.decide">
                  <Button
                    id="budget-review-profile-return"
                    variant="secondary"
                    leading={<Undo2 className="h-4 w-4" aria-hidden="true" />}
                    onClick={() =>
                      void run(
                        budgetReviewApi.returnForUpdate,
                        'Could not return this plan for update.',
                      )
                    }
                    disabled={running}
                    loading={running}
                    loadingLabel="Returning"
                  >
                    Return for update
                  </Button>
                </PermissionGate>
              }
            />
          ) : (
            <ProfileBanner
              id="budget-review-profile-banner"
              icon={<ThumbsDown className="h-4 w-4" aria-hidden="true" />}
              message="This plan was rejected. Returning it to the departments for update reopens the submission window and leaves every ceiling exactly as it is."
              action={
                <PermissionGate permission="budget-review.decide">
                  <Button
                    id="budget-review-profile-return"
                    variant="secondary"
                    leading={<Undo2 className="h-4 w-4" aria-hidden="true" />}
                    onClick={() =>
                      void run(
                        budgetReviewApi.returnForUpdate,
                        'Could not return this plan for update.',
                      )
                    }
                    disabled={running}
                    loading={running}
                    loadingLabel="Returning"
                  >
                    Return for update
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            /*
              ONE NUMBER, through `<CountBadge>` — never inline `(N)` parens, and never a
              PAIR. It read `Budget Proposals (0/3)`, and the denominator was already on
              screen twice: the header's secondary line states `0/3 departments priced` and
              the banner says it again in prose. One value in three slots.

              The number is PRICED, not the roster's length — how many offices proposed is
              what the reader sees the moment they open the tab, where how far the review has
              got is not. A zero renders, which is what says nothing has been priced yet.
            */
            key: 'proposals',
            label: (
              <>
                Budget Proposals <CountBadge count={pricedCount} />
              </>
            ),
          },
          {
            /*
              EXPENDITURES — the reference's third folder, and the plan-level view of every
              office's lines at once.

              NO COUNT BADGE: the figure a reader wants here is money rather than a row count,
              and a badge over "how many budgeted lines exist across sixty departments" is a
              number nobody is scanning the strip for. It is stated inside the tab, beside the
              total it belongs to.

              `ppas.read`, which is the key of what the tab READS — the roster above it is part
              of reading a plan, where this is the PPA module's own content. Gated rather than
              disabled: `<FolderTabs>` hides it and lands a reader who lacks the key on the
              first tab they can see.
            */
            key: 'expenditures',
            label: 'Expenditures',
            permission: 'ppas.read',
          },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
        // The proposals tab is a table owning its own toolbar and pager, so it needs the panel
        // edge to edge; a stack of cards would cap it to a readable measure it does not want.
        panel={tab === 'proposals' ? 'bleed' : 'cards'}
      >
        {tab === 'info' ? (
          <>
            {/*
              NO Edit anywhere on this page, and that is the module's shape rather than
              unfinished work — `<ProfileCard>`'s second exemption, a card whose state is moved
              by an ACTION rather than by typing. The plan's own frame belongs to the Budget
              Plan module and is corrected there; what this one changes is the stage, which the
              banner's three verdicts move.
            */}
            {/*
              THE REFERENCE'S OWN ROW SET, in its order: plan number, fiscal year, status,
              total budget, departments, submission date, coverage, then the two free-text
              fields where the record carries them.

              Three rows came OFF it — departments priced, proposed total, approved total — and
              they were the same values the header states on its secondary line, three inches
              above, on every tab. That is §8 on one screen, and the header is the copy that
              keeps them: it is on screen while the reader is reading the proposals, where a
              card is not.

              The word is `Stage`, not the reference's `Status`. `status` is the ROW's own
              live-or-deleted state everywhere in this product — the pill in the header, the
              column on the register — so the lifecycle takes the register's own header word.
              One word for one concept, product-wide.
            */}
            <ProfileCard id="budget-review-summary" title="Budget plan">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Plan number', value: plan.planNumber, mono: true },
                  { label: 'Fiscal year', value: String(plan.fiscalYear), mono: true },
                  { label: 'Stage', value: plan.statusLabel },
                  { label: 'Total budget', value: formatMoney(proposedTotal), mono: true },
                  { label: 'Departments', value: String(roster.length), mono: true },
                  {
                    label: 'Submission date',
                    // A CALENDAR date — `formatDateOnly`, never an instant formatter.
                    value: formatDateOnly(plan.submissionClosesAt),
                    mono: true,
                  },
                  {
                    label: 'Coverage',
                    value: `${formatDateOnly(plan.coverageStartDate)} – ${formatDateOnly(plan.coverageEndDate)}`,
                    mono: true,
                  },
                  // Every row renders whatever the value — `null` draws the em dash, and
                  // dropping the row would make the card a different shape per record.
                  { label: 'Description', value: plan.description },
                  { label: 'Notes', value: plan.notes },
                ]}
              />
            </ProfileCard>

            {/* Audit is never editable: created and updated are a record of what happened. */}
            <ProfileCard id="budget-review-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: plan.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(plan.createdAt)} className="font-mono">
                        {formatDateTime(plan.createdAt)}
                      </time>
                    ),
                  },
                  { label: 'Updated by', value: plan.updatedByName },
                  {
                    label: 'Updated',
                    value: plan.updatedAt ? (
                      <time dateTime={toIso(plan.updatedAt)} className="font-mono">
                        {formatDateTime(plan.updatedAt)}
                      </time>
                    ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : tab === 'expenditures' ? (
          // MOUNTED by the tab, which is how it stays lazy: a reader who never opens this
          // folder never pays for the roll-up query.
          <PlanExpendituresPanel planId={plan.id} />
        ) : (
          <PpaRosterPanel
            detail={detail}
            roster={roster}
            onChanged={() => load()}
            onSetCeiling={(row) => setPricing(row)}
          />
        )}
      </ProfileHeaderShell>

      {pricing && id ? (
        <BudgetReviewCeilingModal
          open
          budgetPlanId={id}
          orgUnitId={pricing.orgUnitId}
          orgUnitName={pricing.orgUnitName}
          onClose={() => setPricing(null)}
          onSaved={() => {
            setPricing(null);
            // The write moves the header counts AND the row, so the PAGE refetches. The
            // ceiling can also move the plan's own stage — the first one starts the review.
            load();
          }}
        />
      ) : null}

      {/*
        Scoped by `entityId` ALONE, with no `entityTypes` beside it. Adding the type bound on
        top only subtracts: a write the server REFUSED never reached an entity, carries no
        `entityType`, and is exactly the event a reader opens a record's history to find.
      */}
      {historyOpen && id ? (
        <HistoryLogsModal
          open
          module="budget-review"
          entityId={id}
          onClose={() => setHistoryOpen(false)}
        />
      ) : null}
    </div>
  );
}
